'use client';

import {
  OrderLineItem,
  OrderLineItemAgeBadge,
  OrderLineItemCopy,
  OrderLineItemLead,
  OrderLineItemMedia,
  OrderLineItemName,
  OrderLineItemPrice,
  OrderLineItemQuantityRow,
  OrderLineItemContainerVariant,
  OrderLineItemVariant,
} from '@vs-shop/commerce-ui/orderLineItem';
import { IconAgeRestriction } from '@vs-shop/ui/icons';
import { cartPendingTraceVariants } from '@vs-shop/commerce-ui/cartDrawer';
import { BigDecimal } from 'effect';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { CurrencyT } from '../../../../../config/currency';

import ProductAmountCounterCart from '../../../product/ProductAmountCounterCart';
import { CartDrawerProjectedQuantity } from './CartDrawerProjectedQuantity';
import { IntlMoney } from '../../../../server/intl/Intl';

import type { CartMutationItem } from '../../../../../effect/bind/cartMutationItem';
import type { Price } from '@vs-shop/schemas/primitive/price';
import type { CartDrawerPendingStatus } from '@vs-shop/engine/module/cart/cartDrawerProjection';
import type { OrderItem } from '../../../../../effect/schema/branded/orderTightened';

type BaseProps = {
  mutation: CartMutationItem;
  productName: string;
  image: ReactNode;
  ageRestricted: boolean;
  totalPrice: number;
  currency: CurrencyT;
};

type PendingAddition = {
  readonly quantity: OrderItem['quantity'];
  readonly totalPrice: Price;
  readonly status: CartDrawerPendingStatus;
};

type Props = BaseProps &
  (
    | { readonly state: 'confirmed' }
    | { readonly state: 'pending'; readonly pendingAddition: PendingAddition }
  );

export function CartDrawerItem(props: Props) {
  const { mutation, productName, image, ageRestricted, totalPrice, currency } = props;
  const pendingAddition = props.state === 'pending' ? props.pendingAddition : undefined;
  const pendingStatus = pendingAddition?.status ?? 'adding';
  const intl = useIntl();
  const projectedQuantity = mutation.quantity + (pendingAddition?.quantity ?? 0);
  const [quantity, setQuantity] = useState<number>(projectedQuantity);
  // Keep the focused input mounted while its projected cart quantity changes.
  const [lastProjectedQuantity, setLastProjectedQuantity] = useState<number>(projectedQuantity);
  if (lastProjectedQuantity !== projectedQuantity) {
    setLastProjectedQuantity(projectedQuantity);
    setQuantity(projectedQuantity);
  }
  const hasPendingAddition = pendingAddition !== undefined && pendingAddition.quantity > 0;
  const isResolvingPendingAddition = hasPendingAddition && pendingStatus !== 'unresolved';
  const projectedTotalPrice = hasPendingAddition
    ? totalPrice +
      BigDecimal.toNumberUnsafe(
        BigDecimal.multiply(pendingAddition.totalPrice, BigDecimal.fromNumberUnsafe(100))
      )
    : totalPrice;
  const quantityControl = hasPendingAddition ? (
    <CartDrawerProjectedQuantity
      confirmedQuantity={mutation.quantity}
      pendingQuantity={pendingAddition.quantity}
    />
  ) : (
    <ProductAmountCounterCart amount={quantity} setAmountAction={setQuantity} mutation={mutation} />
  );

  const quantityAndPrice = (
    <>
      {quantityControl}
      <OrderLineItemPrice
        variant={OrderLineItemVariant.DRAWER}
        data-provisional={hasPendingAddition ? 'true' : undefined}
      >
        {hasPendingAddition && <span aria-hidden="true">≈ </span>}
        <IntlMoney
          value={{
            amount: projectedTotalPrice,
            currency,
          }}
        />
      </OrderLineItemPrice>
    </>
  );

  return (
    <OrderLineItem
      variant={OrderLineItemContainerVariant.DRAWER}
      aria-busy={isResolvingPendingAddition}
      className={cartPendingTraceVariants()}
    >
      <OrderLineItemLead variant={OrderLineItemVariant.DRAWER}>
        <OrderLineItemMedia variant={OrderLineItemVariant.DRAWER}>
          {image}

          {ageRestricted && (
            <OrderLineItemAgeBadge>
              <IconAgeRestriction className="size-[25px] tab:size-[35px]" />
            </OrderLineItemAgeBadge>
          )}
        </OrderLineItemMedia>
        <OrderLineItemCopy variant={OrderLineItemVariant.DRAWER}>
          <OrderLineItemName variant={OrderLineItemVariant.DRAWER}>{productName}</OrderLineItemName>
          {hasPendingAddition && (
            <span role="status" aria-live="polite" className="sr-only">
              {pendingStatus === 'adding'
                ? intl.formatMessage(
                    {
                      id: 'cartDrawer.pendingLine.addingQuantity',
                      defaultMessage: 'Přidávám {quantity} ks',
                    },
                    { quantity: pendingAddition.quantity }
                  )
                : intl.formatMessage(
                    pendingStatus === 'verifying'
                      ? { id: 'cartDrawer.pendingLine.verifying', defaultMessage: 'Ověřuji košík' }
                      : {
                          id: 'cartDrawer.pendingLine.unresolved',
                          defaultMessage: 'Změnu košíku se nepodařilo ověřit',
                        }
                  )}
            </span>
          )}
          <OrderLineItemQuantityRow className="hidden tab:flex">
            {quantityAndPrice}
          </OrderLineItemQuantityRow>
        </OrderLineItemCopy>
      </OrderLineItemLead>
      <OrderLineItemQuantityRow className="tab:hidden">{quantityAndPrice}</OrderLineItemQuantityRow>
    </OrderLineItem>
  );
}
